import React, { useEffect, useState, useRef } from 'react';
import { useCampaign } from '../../contexts/CampaignContext';
import { playRollSound } from '../../lib/diceSounds';
import { formatFormula } from '../../lib/dice';

const TUMBLE_MS  = 900;
const SETTLE_MS  = 200;

// How long a roll record stays in Firestore (and the log) before clients prune it.
const PRUNE_MS   = 1000 * 60 * 30; // 30 min
// Also cap log size: if more than LOG_LIMIT entries exist on this device,
// the oldest get pruned so listeners stay lean.
const LOG_LIMIT  = 100;

export default function DiceRollOverlay({ withSound = false, large = false, viewerIdentity = null }) {
  const { diceRolls, deleteDiceRoll, dismissDiceRoll } = useCampaign();
  const [now, setNow] = useState(Date.now());
  const playedRef = useRef(new Set());
  const prunedRef = useRef(new Set());

  // Tick to drive the tumble animation; stops once nothing is tumbling.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 80);
    return () => clearInterval(id);
  }, []);

  // Hidden DM rolls: only the DM viewer sees them on screen / hears the
  // tumble sound. Display TV (no viewerIdentity) and player views skip them.
  const visibleForViewer = (roll) => !roll.hidden || !!viewerIdentity?.isDm;

  // Fire sound exactly once for new rolls (Display only).
  useEffect(() => {
    if (!withSound) return;
    for (const roll of Object.values(diceRolls)) {
      if (playedRef.current.has(roll.id)) continue;
      const age = Date.now() - (roll.createdAt || 0);
      if (age >= 0 && age < 500) {
        if (visibleForViewer(roll)) playRollSound(roll);
        playedRef.current.add(roll.id);
      } else {
        // Old roll loaded on initial snapshot — don't replay sound.
        playedRef.current.add(roll.id);
      }
    }
  }, [diceRolls, withSound, viewerIdentity?.isDm]);

  // Prune expired rolls from Firestore so the log stays bounded.
  useEffect(() => {
    const all = Object.values(diceRolls);
    if (all.length === 0) return;

    const tooOld = all.filter((r) => now - (r.createdAt || 0) > PRUNE_MS);
    const sorted = [...all].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
    const overflow = sorted.slice(LOG_LIMIT);
    const toDelete = [...tooOld, ...overflow];

    for (const r of toDelete) {
      if (prunedRef.current.has(r.id)) continue;
      prunedRef.current.add(r.id);
      deleteDiceRoll(r.id).catch(() => prunedRef.current.delete(r.id));
    }
  }, [diceRolls, now, deleteDiceRoll]);

  // Active = not yet dismissed by the roller. The roll lives in the log for
  // the rest of the session via the prune-window above. Hidden rolls are
  // skipped for non-DM viewers.
  const active = Object.values(diceRolls)
    .filter((r) => !r.dismissedAt && visibleForViewer(r))
    .sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));

  if (active.length === 0) return null;

  return (
    <div style={{ ...styles.viewport, ...(large ? styles.viewportLarge : styles.viewportBottom) }}>
      {active.map((roll) => {
        const isOwn =
          viewerIdentity?.isDm && roll.isDm
            ? true
            : !!viewerIdentity?.playerId && viewerIdentity.playerId === roll.playerId;
        // DM gets table-janitor power: can dismiss any roll, including legacy
        // ones predating the playerId/dismissedAt fields.
        const canDismiss = isOwn || !!viewerIdentity?.isDm;
        return (
          <RollCard
            key={roll.id}
            roll={roll}
            now={now}
            large={large}
            canDismiss={canDismiss}
            onDismiss={() => dismissDiceRoll(roll.id)}
          />
        );
      })}
    </div>
  );
}

function RollCard({ roll, now, large, canDismiss, onDismiss }) {
  const age      = now - (roll.createdAt || 0);
  const tumbling = age < TUMBLE_MS;

  // Show all rolled dice for adv/dis, only kept for normal. Highlight the selected ones after tumble.
  let diceToShow = [];
  for (const g of roll.dice || []) {
    // For advantage/disadvantage, show all results; for normal, show only kept
    const valuesToShow = (roll.mode === 'advantage' || roll.mode === 'disadvantage') ? g.results : g.kept;
    for (const v of valuesToShow) {
      const isKept = g.kept.includes(v);
      diceToShow.push({
        value: v,
        sides: g.sides,
        showWinGlow: !tumbling && isKept,
        glowColor: roll.mode === 'disadvantage' ? '#c03333' : '#d4a847'
      });
    }
  }

  const dieSize  = large ? 72 : 48;
  const fontSize = large ? 36 : 24;

  const tone =
    roll.crit === 'crit'    ? styles.toneCrit
    : roll.crit === 'fumble'? styles.toneFumble
    : null;

  return (
    <div style={{ ...styles.card, ...tone }}>
      <div style={styles.header}>
        <span style={{ ...styles.dot, background: roll.colour || '#c084fc' }} />
        <span style={styles.name}>{roll.playerName || (roll.isDm ? 'DM' : 'Player')}</span>
        <span style={styles.formula}>{formatFormula(roll)}</span>
        {canDismiss && (
          <button
            style={styles.dismissBtn}
            onClick={onDismiss}
            title="Dismiss the dice (closes for everyone)"
          >✕</button>
        )}
      </div>
      <div style={styles.diceRow}>
        {diceToShow.map((die, i) => (
          <Die
            key={i}
            value={die.value}
            sides={die.sides}
            showWinGlow={die.showWinGlow}
            glowColor={die.glowColor}
            tumbling={tumbling}
            seed={roll.id + ':' + i}
            colour={roll.colour || '#c084fc'}
            size={dieSize}
            fontSize={fontSize}
          />
        ))}
      </div>
      {!tumbling && (
        <div style={{ ...styles.totalRow, animation: `toastSlideIn 200ms ease-out` }}>
          <span style={styles.totalLabel}>Total</span>
          <span style={{ ...styles.totalValue, fontSize: large ? 44 : 28 }}>{roll.total}</span>
          {roll.crit === 'crit'   && <span style={{ ...styles.critTag, background: '#facc15', color: '#1a0e00' }}>CRIT!</span>}
          {roll.crit === 'fumble' && <span style={{ ...styles.critTag, background: '#c03333', color: '#fff' }}>FUMBLE</span>}
        </div>
      )}
    </div>
  );
}

function findSides(roll, flatIdx) {
  let i = flatIdx;
  for (const g of roll.dice || []) {
    if (i < g.kept.length) return g.sides;
    i -= g.kept.length;
  }
  return 20;
}

function Die({ value, sides, showWinGlow, glowColor, tumbling, seed, colour, size, fontSize }) {
  // Tumble: rapid-cycle random faces while `tumbling` is true.
  const [face, setFace] = useState(value);
  useEffect(() => {
    if (!tumbling) { setFace(value); return; }
    let stopped = false;
    const tick = () => {
      if (stopped) return;
      setFace(Math.floor(Math.random() * sides) + 1);
    };
    const id = setInterval(tick, 60);
    // Settle a touch before tumble end so the final value reads cleanly.
    const settle = setTimeout(() => { stopped = true; clearInterval(id); setFace(value); }, TUMBLE_MS - SETTLE_MS);
    return () => { stopped = true; clearInterval(id); clearTimeout(settle); };
  }, [tumbling, sides, value, seed]);

  const isD20Crit   = sides === 20 && value === 20;
  const isD20Fumble = sides === 20 && value === 1;

  // Convert hex glowColor to RGBA for the glow effect
  const getGlowRgba = (hex) => {
    const h = hex.replace('#', '');
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return `rgba(${r},${g},${b},0.6)`;
  };

  const glowRgba = glowColor ? getGlowRgba(glowColor) : 'rgba(212,168,71,0.6)';

  return (
    <div
      style={{
        ...styles.die,
        width: size, height: size, fontSize,
        background: `linear-gradient(180deg, ${colour}, ${darken(colour, 0.6)})`,
        boxShadow: isD20Crit
          ? '0 0 20px 6px rgba(250,204,21,0.7), 0 4px 14px rgba(0,0,0,0.45)'
          : isD20Fumble
          ? '0 0 18px 6px rgba(192,51,51,0.7), 0 4px 14px rgba(0,0,0,0.45)'
          : showWinGlow
          ? `0 0 16px 3px ${glowRgba}, 0 4px 14px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.3)`
          : '0 4px 14px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.3)',
        animation: tumbling ? 'diceTumble 700ms ease-out infinite' : 'none',
      }}
    >
      <span style={styles.dieFace}>{face}</span>
      <span style={styles.dieSides}>d{sides}</span>
    </div>
  );
}

function darken(hex, amt) {
  // crude HSL-ish darken; #rrggbb only
  const h = hex.replace('#', '');
  const r = Math.max(0, Math.min(255, parseInt(h.slice(0, 2), 16) * amt));
  const g = Math.max(0, Math.min(255, parseInt(h.slice(2, 4), 16) * amt));
  const b = Math.max(0, Math.min(255, parseInt(h.slice(4, 6), 16) * amt));
  const to2 = (n) => Math.floor(n).toString(16).padStart(2, '0');
  return `#${to2(r)}${to2(g)}${to2(b)}`;
}

const styles = {
  viewport: {
    position: 'fixed',
    // Above any open dice tray (DM tray is z-201) so live rolls remain
    // visible even while the DM/player has the tray pulled up.
    zIndex: 250,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  // Player & DM views: pin to bottom-centre, sitting just above where the
  // dice tray slides up from. The roll appears right above the dice menu.
  viewportBottom: {
    bottom: 120,
    left: '50%',
    transform: 'translateX(-50%)',
    width: 'min(480px, calc(100vw - 24px))',
    alignItems: 'stretch',
  },
  // Display view: large, centred over the map for table-side drama.
  viewportLarge: {
    top: '50%', right: '50%',
    transform: 'translate(50%, -50%)',
    maxWidth: 'min(720px, 92vw)',
    alignItems: 'center',
  },
  card: {
    background: 'linear-gradient(180deg, rgba(20,32,56,0.96), rgba(10,15,30,0.96))',
    border: '2px solid #8b7340',
    borderRadius: 8,
    padding: '10px 14px 12px',
    boxShadow: '0 12px 32px rgba(0,0,0,0.7)',
    color: '#f0e6d0',
    fontFamily: "'Press Start 2P', monospace",
    transition: 'opacity 280ms ease',
    pointerEvents: 'auto',
  },
  dismissBtn: {
    background: 'transparent',
    border: '1px solid #4a3d28',
    borderRadius: 4,
    color: '#9a8b6e',
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer',
    width: 24,
    height: 24,
    padding: 0,
    marginLeft: 4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  toneCrit:   { borderColor: '#facc15', boxShadow: '0 0 24px rgba(250,204,21,0.45), 0 12px 32px rgba(0,0,0,0.7)' },
  toneFumble: { borderColor: '#c03333', boxShadow: '0 0 24px rgba(192,51,51,0.45), 0 12px 32px rgba(0,0,0,0.7)' },
  header: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 },
  dot:    { width: 12, height: 12, borderRadius: '50%', border: '1px solid rgba(0,0,0,0.5)', flexShrink: 0 },
  name:   { fontSize: 13, fontWeight: 700, color: '#d4a847' },
  formula:{ fontSize: 11, color: '#9a8b6e', marginLeft: 'auto' },
  diceRow:{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' },
  die: {
    position: 'relative',
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 800,
    color: '#fff',
    textShadow: '0 1px 3px rgba(0,0,0,0.85)',
    border: '1px solid rgba(0,0,0,0.45)',
  },
  dieFace:  { lineHeight: 1, fontFamily: "'Press Start 2P', monospace" },
  dieSides: { position: 'absolute', bottom: 3, right: 5, fontSize: 9, fontWeight: 700, opacity: 0.65, letterSpacing: '0.02em' },
  totalRow: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, justifyContent: 'center' },
  totalLabel: { fontSize: 11, color: '#9a8b6e', textTransform: 'uppercase', letterSpacing: '0.08em' },
  totalValue: { fontWeight: 800, color: '#d4a847', fontFamily: "'Press Start 2P', monospace" },
  critTag: { fontSize: 11, fontWeight: 800, padding: '2px 8px', borderRadius: 3, letterSpacing: '0.08em' },
};
